import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ExaminerTreemapTableItem,
  ExaminerType,
  FlatExaminerTreemapTableItem,
  MessageModal,
} from '../../ExaminerTreemap.types';
import {
  Examiner,
  ExaminingTeamChairmen,
  ExaminingTeamMember,
  ExamTaskGroupCoordinator,
  IExaminingTeamMemberWithFunctionId,
  ITaskGroup,
  SampleAssessmentGroup,
  SampleAssessmentGroupMember,
} from '../../../../models/taskGroup.types';
import { getExaminerNumberFromPermission, isExpanded } from '../../Helpers';
import { areObjectsEqual } from '../../../../utils/areObjectsEqual';
import TooltipOne from '../../../../components/TooltipOne/TooltipOne.component';
import { useStyles } from '../ExaminerTreemapTable.styles';
import { ExpandableArrowIcon } from '../ExpandableArrowIcon.component';
import { ActionMenuItem } from '../../../../components/ActionsMenu/ActionsMenu.types';
import ActionsMenu from '../../../../components/ActionsMenu/ActionsMenu.component';
import { ExaminerFunction } from '../../../Assessment/Assessment.types';
import { SubText } from '../../../../components/Prompt/Prompt.component';
import { determineTableSubheaderRowBackgroundColor } from '../helpers';
import { Committee, getCommitteeLabel } from '../../../../models/committees.types';
import { PromptDetails } from '../../../../models/promptsTypeModels.types';
import { BlockadeTypeId } from '../../../../components/ExaminersStructure/ExaminersStructure.types';
import ManualLockedPersonIcon from '@mui/icons-material/LockPersonOutlined';
import { fillRed } from '../../../../utils/contrastSvgAttributes';
import AutoLockedPersonIcon from '../../../../assets/icons/auto-locked-red.svg';
import { HandleClickMenuFunction } from '../ExaminerTreemapTable.types';
import { useExamTaskGroupCoordinatorActionsMenu } from './useExamTaskGroupCoordinatorActionsMenu';
import { useSampleAssessmentGroupMemberActionsMenu } from './useSampleAssessmentGroupMemberActionsMenu';
import { useSampleAssessmentGroupChairmanActionsMenu } from './useSampleAssessmentGroupChairmanActionsMenu';
import { useSampleAssessmentGroupActionsMenu } from './useSampleAssessmentGroupActionsMenu';
import { useExaminingTeamChairmenActionsMenu } from './useExaminingTeamChairmenActionsMenu';
import { useExaminingTeamMemberActionsMenu } from './useExaminingTeamMemberActionsMenu';
import { useVerifierActionsMenu } from './useVerifierActionsMenu';

const treemapItemsPagination = 100;

export type UseExaminerTreemapTableRowsProps = {
  data: ITaskGroup[];
  expandedItems: string[];
  handleExpandedItems: (id?: string) => void;
  examinerMode?: boolean;
  examinerFunction: ExaminerFunction;
  openAssignModal: (
    title: string,
    type: ExaminerType,
    isMultiChoice?: boolean,
    assessmentId?: string,
    examinerFunctionId?: string,
    secondaryId?: string,
  ) => void;
  setPromptSubTexts: React.Dispatch<React.SetStateAction<SubText[] | undefined>>;
  setPromptMainText: React.Dispatch<React.SetStateAction<string>>;
  setIsConfirmationPromptVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedItemId: React.MutableRefObject<string>;
  selectedTaskGroup: React.MutableRefObject<ITaskGroup | undefined>;
  selectedAssessmentSessionId: React.MutableRefObject<string>;
  setMessageModal: React.Dispatch<React.SetStateAction<MessageModal>>;
  setEditExaminerNumberModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAssignSampleAssessmentGroupChairmanDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
  hasPermissionForExamTaskGroupCoordinatorActions: boolean;
  hasPermissionForGroupTaskActions: boolean;
  setOpenUnassignDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handleReload: () => void;
  setIsErrorPromptVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onModalFunctionAction: (data: PromptDetails) => void;
  closeMenu: () => void;
  setOpenLimitCardPerExaminerDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDisableExaminingTeamPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  setIsExaminingTeamChairmen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDisableExaminerPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenEnableExaminerPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  hasPermissionToManageActions: boolean;
  selectedExaminer: React.MutableRefObject<Examiner | undefined>;
  selectedSecondaryId: React.MutableRefObject<string>;
  examiningTeamMember: React.MutableRefObject<IExaminingTeamMemberWithFunctionId | undefined>;
  isTechAdmin: boolean;
};

export const useExaminerTreemapTableRows = (props: UseExaminerTreemapTableRowsProps) => {
  const {
    expandedItems,
    handleExpandedItems,
    examinerMode,
    examinerFunction,
    openAssignModal,
    selectedItemId,
    setIsConfirmationPromptVisible,
    setPromptMainText,
    setPromptSubTexts,
    selectedTaskGroup,
    selectedAssessmentSessionId,
    setMessageModal,
    setEditExaminerNumberModalOpen,
    setIsAssignSampleAssessmentGroupChairmanDialogVisible,
    hasPermissionForExamTaskGroupCoordinatorActions,
    hasPermissionForGroupTaskActions,
    setOpenUnassignDialog,
    handleReload,
    setIsErrorPromptVisible,
    closeMenu,
    onModalFunctionAction,
    setIsExaminingTeamChairmen,
    setOpenDisableExaminerPrompt,
    setOpenDisableExaminingTeamPrompt,
    setOpenEnableExaminerPrompt,
    setOpenLimitCardPerExaminerDialog,
    hasPermissionToManageActions,
    examiningTeamMember,
    selectedExaminer,
    selectedSecondaryId,
    isTechAdmin,
  } = props;
  const classes = useStyles();
  const allTreemapItemsCount = useRef(0);
  const [action, setAction] = useState<string>('');
  const [openAddKGZPrompt, setOpenKGZPrompt] = useState<boolean>(false);

  const [treemapData, setTreemapData] = useState<ExaminerTreemapTableItem[] | undefined>(undefined);
  const allTreemapDataItems = useRef<FlatExaminerTreemapTableItem>({});
  const [isAddSampleAssessmentGroupConfirmationVisible, setIsAddSampleAssessmentGroupConfirmationVisible] =
    useState<boolean>(false);
  const [renderStructureItemsCount, setRenderStructureItemsCount] = useState(treemapItemsPagination);
  const [selectedSampleAsssessmentGroup, setSelectedSampleAsssessmentGroup] = useState<string>();
  const removedFunctionKgzForGroupIdRef = useRef<string>('');
  const removedFunctionKgzForGroupGroupNameRef = useRef<string>('');
  const removedExaminatorFromZeFullNameRef = useRef<string>('');
  const removedExaminatorFromZeByTeamMemberIdRef = useRef<string>('');

  const getTaskGroupActionsMenu = useCallback(
    (item: ITaskGroup, rowIndex: number): JSX.Element => {
      const menuItems: ActionMenuItem[] = [];
      menuItems.push({
        text: 'Dodaj funkcję KGZ',
        disabled: isTechAdmin,
        onClick: () => {
          setAction('Dodaj funkcję KGZ');
          setOpenKGZPrompt(true);
        },
      });
      if (!item.sampleAssessmentGroup) {
        menuItems.push({
          text: 'Dodaj ZWO',
          onClick: () => {
            setAction('Dodaj ZWO');
            setIsAddSampleAssessmentGroupConfirmationVisible(true);
          },
        });
      }
      return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
    },
    [setAction, isTechAdmin],
  );

  const handleClickMenu: HandleClickMenuFunction = useCallback(
    (
      itemId: string,
      taskGroup?: ITaskGroup,
      examiner?: Examiner,
      secondaryId?: string,
      sampleAssessmentGroupId?: string,
      teamMember?: ExaminingTeamMember,
      item?: any,
      assessmentId?: string,
    ) => {
      selectedItemId.current = itemId;
      if (assessmentId) {
        selectedAssessmentSessionId.current = assessmentId;
      }
      if (taskGroup) {
        selectedTaskGroup.current = taskGroup;
        removedFunctionKgzForGroupIdRef.current = itemId;
        removedFunctionKgzForGroupGroupNameRef.current = taskGroup.name;
      }
      if (examiner) {
        selectedExaminer.current = examiner;
        removedExaminatorFromZeFullNameRef.current = examiner.firstName + ' ' + examiner.lastName;
        removedExaminatorFromZeByTeamMemberIdRef.current = teamMember ? teamMember.teamMemberId : '';
      }
      if (secondaryId) {
        selectedSecondaryId.current = secondaryId;
      }
      if (sampleAssessmentGroupId) {
        setSelectedSampleAsssessmentGroup(sampleAssessmentGroupId);
      }
      if (item) {
        examiningTeamMember.current = item;
      }
    },
    [],
  );

  const getNewRow = function <T>(id: string, rowData: T, getRowGetter: (rowData: T) => ExaminerTreemapTableItem) {
    const prevRowItem = allTreemapDataItems.current[id];

    if (
      areObjectsEqual(prevRowItem?.rowData, rowData as Partial<T>) &&
      prevRowItem?.rowGetterFunction === getRowGetter
    ) {
      return prevRowItem.row;
    }

    const newRow = getRowGetter(rowData);
    allTreemapDataItems.current[id] = {
      rowGetterFunction: getRowGetter as (rowData: unknown) => ExaminerTreemapTableItem,
      row: newRow,
      rowData,
    };
    return newRow;
  };

  const getTaskGroupRow = useCallback(
    (taskGroupRowData: {
      assessmentId: string;
      id: string;
      isExpanded: boolean;
      item: ITaskGroup;
      rowIndex: number;
      tooltipTitle: string;
    }) => {
      const { assessmentId, id, isExpanded, item, rowIndex, tooltipTitle } = taskGroupRowData;

      const newRow = {
        id,
        taskGroupName: (
          <TooltipOne title={tooltipTitle} placement="top">
            <span>
              <div
                className={`${classes.taskGroupHeader} ${isTechAdmin ? classes.taskGroupHeaderAT : ''
                  }`}
              >
                <ExpandableArrowIcon
                  className={classes.dropdownIcon}
                  isExpanded={isExpanded}
                  ariaControls="task-group-header"
                  onClick={() => handleExpandedItems(item.taskGroupId)}
                />
                <TooltipOne title={isTechAdmin ? item.name : ''} placement="right">
                  <span className={isTechAdmin ? classes.taskGroupNameAT : ''}>
                    {item.name}
                  </span>
                </TooltipOne>
              </div>
            </span>
          </TooltipOne>
        ),
        actions: (
          <>
            {hasPermissionForGroupTaskActions && (
              <div
                onClick={() =>
                  handleClickMenu(
                    item.taskGroupId,
                    item,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    assessmentId,
                  )
                }
              >
                {getTaskGroupActionsMenu(item, rowIndex)}
              </div>
            )}
          </>
        ),
        rowBackground: 'rgba(202, 202, 252, 0.2)',
      };
      return newRow;
    },
    [
      classes.dropdownIcon,
      classes.taskGroupHeader,
      getTaskGroupActionsMenu,
      handleClickMenu,
      handleExpandedItems,
      hasPermissionForGroupTaskActions,
    ],
  );

  const { getSampleAssessmentGroupActionsMenu } = useSampleAssessmentGroupActionsMenu({
    data: props.data,
    openAssignModal,
    selectedItemId,
    selectedTaskGroup,
    setAction,
    setIsConfirmationPromptVisible,
    setPromptMainText,
    setPromptSubTexts,
  });

  const getSampleAssessmentGroupRow = useCallback(
    (sampleAssessmentGroupRowData: {
      assessmentId: string;
      id: string;
      isExpanded: boolean;
      taskGroup: ITaskGroup;
      rowIndex: number;
      tooltipTitle: string;
      itemIsSelected: boolean;
    }) => {
      const { assessmentId, id, isExpanded, taskGroup, rowIndex, tooltipTitle, itemIsSelected } =
        sampleAssessmentGroupRowData;
      const newRow = {
        id,
        function: (
          <TooltipOne title={tooltipTitle} placement="top">
            <div className={classes.subHeader}>
              <ExpandableArrowIcon
                className={classes.dropdownIcon}
                isExpanded={isExpanded}
                ariaControls={'SampleAssessmentGroup-header'}
                onClick={() => handleExpandedItems(taskGroup.sampleAssessmentGroup?.sampleAssessmentsGroupId)}
              />
              Zespół Wzorcowych Ocen
            </div>
          </TooltipOne>
        ),
        actions: (
          <>
            {hasPermissionForGroupTaskActions && (
              <div
                onClick={() => {
                  if (taskGroup.sampleAssessmentGroup) {
                    handleClickMenu(
                      taskGroup.sampleAssessmentGroup.sampleAssessmentsGroupId,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      assessmentId,
                    );
                  }
                }}
              >
                {getSampleAssessmentGroupActionsMenu(taskGroup, assessmentId, rowIndex)}
              </div>
            )}
          </>
        ),
        rowBackground: determineTableSubheaderRowBackgroundColor(itemIsSelected /*id === selectedItemId*/),
      };
      return newRow;
    },
    [
      classes.dropdownIcon,
      classes.subHeader,
      getSampleAssessmentGroupActionsMenu,
      handleClickMenu,
      handleExpandedItems,
      hasPermissionForGroupTaskActions,
    ],
  );

  const determineRowBackgroundColor = useCallback(
    (id: string, shouldBeHighlighted: boolean, assessmentId: string): string => {
      if (
        id !== selectedItemId.current &&
        assessmentId !== selectedAssessmentSessionId.current &&
        shouldBeHighlighted
      ) {
        return 'rgba(202, 202, 252, 0.7)'; // This specific color
      } else {
        return '';
      }
    },
    [],
  );

  const { getSampleAssessmentGroupChairmanActionsMenu } = useSampleAssessmentGroupChairmanActionsMenu({
    data: props.data,
    selectedItemId,
    selectedTaskGroup,
    setAction,
    setEditExaminerNumberModalOpen,
    setIsAssignSampleAssessmentGroupChairmanDialogVisible,
    setIsConfirmationPromptVisible,
    setMessageModal,
    setPromptMainText,
    setPromptSubTexts,
  });

  const getSampleAssessmentChairmanRow = useCallback(
    (sampleAssessmentChairmanRowData: {
      assessmentId: string;
      examinerNumber: string;
      examCommittee: string;
      firstName: string;
      id: string;
      lastName: string;
      rowBackground: string;
      rowIndex: number;
      sampleAssessmentGroup: SampleAssessmentGroup;
      taskGroup: ITaskGroup;
    }) => {
      const {
        assessmentId,
        examinerNumber,
        examCommittee,
        firstName,
        id,
        lastName,
        rowBackground,
        rowIndex,
        sampleAssessmentGroup,
        taskGroup,
      } = sampleAssessmentChairmanRowData;
      const itemId = sampleAssessmentGroup.sampleAssessmentChairman
        ? sampleAssessmentGroup.sampleAssessmentChairman.sampleAssessmentChairmanId
        : sampleAssessmentGroup.sampleAssessmentsGroupId;
      return {
        id,
        function: <div style={{ transform: 'translateX(40px)' }}>Przewodniczący ZWO</div>,
        firstName,
        lastName,
        examinerNumber,
        examCommittee,
        actions: (
          <>
            {hasPermissionForGroupTaskActions && (
              <div
                onClick={() => {
                  if (taskGroup.sampleAssessmentGroup) {
                    handleClickMenu(
                      itemId,
                      taskGroup,
                      taskGroup.sampleAssessmentGroup.sampleAssessmentChairman.examiner,
                      undefined,
                      taskGroup.sampleAssessmentGroup.sampleAssessmentsGroupId,
                      undefined,
                      taskGroup.sampleAssessmentGroup.sampleAssessmentChairman,
                      assessmentId,
                    );
                  }
                }}
              >
                {getSampleAssessmentGroupChairmanActionsMenu(taskGroup, rowIndex)}
              </div>
            )}
          </>
        ),
        rowBackground,
      };
    },
    [getSampleAssessmentGroupChairmanActionsMenu, handleClickMenu, hasPermissionForGroupTaskActions],
  );

  const { getSampleAssessmentGroupMemberActionsMenu } = useSampleAssessmentGroupMemberActionsMenu({
    data: props.data,
    hasPermissionForGroupTaskActions,
    selectedItemId,
    selectedTaskGroup,
    setAction,
    setEditExaminerNumberModalOpen,
    setIsConfirmationPromptVisible,
    setMessageModal,
    setPromptMainText,
    setPromptSubTexts,
  });

  const getSampleAssessmentMemberRow = useCallback(
    (sampleAssessmentMemberRowData: {
      member: SampleAssessmentGroupMember;
      assessmentId: string;
      taskGroup: ITaskGroup;
      rowIndex: number;
      rowBackground: string;
    }) => {
      const { member, assessmentId, taskGroup, rowIndex, rowBackground } = sampleAssessmentMemberRowData;

      return {
        id: member.sampleAssessmentMemberId,
        function: (
          <div
            style={{
              transform: 'translateX(40px)',
            }}
          >
            Członek ZWO
          </div>
        ),
        firstName: member.examiner.firstName,
        lastName: member.examiner.lastName,
        examinerNumber: getExaminerNumberFromPermission(member.examiner, assessmentId),
        examCommittee: getCommitteeLabel(member.examiner?.examinerPermission[0]?.examCommitteeId),
        actions: (
          <>
            {hasPermissionForGroupTaskActions && (
              <div
                onClick={() => {
                  if (taskGroup.sampleAssessmentGroup) {
                    handleClickMenu(
                      member.sampleAssessmentMemberId,
                      taskGroup,
                      member.examiner,
                      undefined,
                      undefined,
                      undefined,
                      member,
                      assessmentId,
                    );
                  }
                }}
              >
                {getSampleAssessmentGroupMemberActionsMenu(rowIndex)}
              </div>
            )}
          </>
        ),
        rowBackground,
      };
    },
    [getSampleAssessmentGroupMemberActionsMenu, handleClickMenu, hasPermissionForGroupTaskActions],
  );

  const { getExamTaskGroupCoordinatorActionsMenu } = useExamTaskGroupCoordinatorActionsMenu({
    closeMenu,
    data: props.data,
    examinerFunction,
    handleReload,
    hasPermissionForGroupTaskActions,
    onModalFunctionAction,
    openAssignModal,
    removedFunctionKgzForGroupGroupNameRef,
    removedFunctionKgzForGroupIdRef,
    selectedItemId,
    selectedTaskGroup,
    setAction,
    setEditExaminerNumberModalOpen,
    setIsErrorPromptVisible,
    setMessageModal,
    setOpenUnassignDialog,
    setPromptMainText,
    setPromptSubTexts,
    isTechAdmin,
  });

  const getExamTaskGroupCoordinatorRow = useCallback(
    (examTaskGroupCoordinatorRowData: {
      id: string;
      firstName: string;
      lastName: string;
      examinerNumber: string;
      examCommittee: string;
      assessmentId: string;
      tooltipTitle: string;
      examTaskGroupCoordinator: ExamTaskGroupCoordinator;
      isExpanded: boolean;
      rowBackground: string;
      rowIndex: number;
      taskGroup: ITaskGroup;
    }) => {
      const {
        id,
        firstName,
        lastName,
        examinerNumber,
        examCommittee,
        tooltipTitle,
        isExpanded,
        examTaskGroupCoordinator,
        taskGroup,
        assessmentId,
        rowBackground,
        rowIndex,
      } = examTaskGroupCoordinatorRowData;
      const showMinusIcon = examTaskGroupCoordinator.examiningTeamChairmen.length === 0;
      return {
        id,
        function: (
          <TooltipOne title={showMinusIcon ? '' : tooltipTitle} placement="top">
            <div className={classes.subHeader}>
              <ExpandableArrowIcon
                className={classes.dropdownIcon}
                isExpanded={isExpanded}
                forceMinusIcon={showMinusIcon}
                ariaControls={'KGZ-header'}
                onClick={() => handleExpandedItems(examTaskGroupCoordinator.taskGroupCoordinatorId)}
              />
              Koordynator grupy zadań
            </div>
          </TooltipOne>
        ),
        firstName,
        lastName,
        examinerNumber,
        examCommittee,
        actions: (
          <>
            {hasPermissionForExamTaskGroupCoordinatorActions && (
              <div
                onClick={() => {
                  handleClickMenu(
                    examTaskGroupCoordinator.taskGroupCoordinatorId,
                    taskGroup,
                    examTaskGroupCoordinator.examiner,
                    undefined,
                    undefined,
                    undefined,
                    examTaskGroupCoordinator,
                    assessmentId,
                  );
                }}
              >
                {getExamTaskGroupCoordinatorActionsMenu(examTaskGroupCoordinator, assessmentId, rowIndex)}
              </div>
            )}
          </>
        ),
        rowBackground,
      };
    },
    [
      classes.dropdownIcon,
      classes.subHeader,
      getExamTaskGroupCoordinatorActionsMenu,
      handleClickMenu,
      handleExpandedItems,
      hasPermissionForExamTaskGroupCoordinatorActions,
      isTechAdmin,
    ],
  );

  const { getExaminingTeamChairmenActionsMenu } = useExaminingTeamChairmenActionsMenu({
    data: props.data,
    openAssignModal,
    selectedItemId,
    selectedTaskGroup,
    setAction,
    setEditExaminerNumberModalOpen,
    setIsConfirmationPromptVisible,
    setIsExaminingTeamChairmen,
    setMessageModal,
    setOpenDisableExaminerPrompt,
    setOpenDisableExaminingTeamPrompt,
    setOpenEnableExaminerPrompt,
    setOpenLimitCardPerExaminerDialog,
    setPromptMainText,
    setPromptSubTexts,
    examinerMode,
  });

  const getExaminingTeamChairmenRow = useCallback(
    (examiningTeamChairmenRowData: {
      id: string;
      isRowExpanded: boolean;
      tooltipTitle: string;
      firstName: string;
      lastName: string;
      examinerNumber: string;
      examCommittee: string;
      isBlocked: BlockadeTypeId | undefined;
      examiningTeamChairmen: ExaminingTeamChairmen;
      assessmentId: string;
      rowBackground: string;
      taskGroup: ITaskGroup;
      rowIndex: number;
    }) => {
      const {
        id,
        isRowExpanded,
        tooltipTitle,
        firstName,
        lastName,
        examinerNumber,
        examCommittee,
        isBlocked,
        examiningTeamChairmen,
        taskGroup,
        assessmentId,
        rowIndex,
        rowBackground,
      } = examiningTeamChairmenRowData;
      const showMinusIcon = examiningTeamChairmen.examiningTeam?.examiningTeamMembers?.length === 0;

      return {
        id,
        function: (
          <TooltipOne title={showMinusIcon ? '' : tooltipTitle} placement="top">
            <div
              className={classes.subHeader}
              style={{
                transform: 'translateX(20px)',
              }}
            >
              <ExpandableArrowIcon
                className={classes.dropdownIcon}
                isExpanded={isRowExpanded}
                forceMinusIcon={showMinusIcon}
                ariaControls={'PZE-header'}
                onClick={() => handleExpandedItems(id)}
              />
              PZE
            </div>
          </TooltipOne>
        ),
        firstName,
        lastName,
        examinerNumber: (
          <div className={classes.columnWithLockIcon}>
            {examinerNumber}
            {isBlocked === BlockadeTypeId.Manual && (
              <TooltipOne title="Zablokowany ręcznie" placement="top">
                <ManualLockedPersonIcon
                  className={classes.lockIcon}
                  style={{
                    color: fillRed,
                  }}
                />
              </TooltipOne>
            )}
            {isBlocked === BlockadeTypeId.Automatic && (
              <TooltipOne title="Zablokowany automatycznie" placement="top">
                <img
                  src={AutoLockedPersonIcon}
                  className={classes.autolockIcon}
                  alt="ikona blokady automatycznej"
                  style={{
                    color: fillRed,
                  }}
                />
              </TooltipOne>
            )}
          </div>
        ),
        examCommittee,
        actions: (
          <>
            {hasPermissionToManageActions && (
              <div
                onClick={() => {
                  handleClickMenu(
                    examiningTeamChairmen.teamChairmanId,
                    taskGroup,
                    examiningTeamChairmen.examiner,
                    examiningTeamChairmen.examiningTeam.examiningTeamId,
                    undefined,
                    undefined,
                    examiningTeamChairmen,
                    assessmentId,
                  );
                }}
              >
                {getExaminingTeamChairmenActionsMenu(examiningTeamChairmen, assessmentId, rowIndex)}
              </div>
            )}
          </>
        ),
        rowBackground,
      };
    },
    [
      classes.autolockIcon,
      classes.columnWithLockIcon,
      classes.dropdownIcon,
      classes.lockIcon,
      classes.subHeader,
      getExaminingTeamChairmenActionsMenu,
      handleClickMenu,
      handleExpandedItems,
      hasPermissionToManageActions,
    ],
  );

  const { getExaminingTeamMemberActionsMenu } = useExaminingTeamMemberActionsMenu({
    onModalFunctionAction,
    removedExaminatorFromZeByTeamMemberIdRef,
    removedExaminatorFromZeFullNameRef,
    setAction,
    setEditExaminerNumberModalOpen,
    setIsExaminingTeamChairmen,
    setMessageModal,
    setOpenDisableExaminerPrompt,
    setOpenEnableExaminerPrompt,
  });

  const { getVerifierActionsMenu } = useVerifierActionsMenu({
    data: props.data,
    selectedItemId,
    selectedTaskGroup,
    setAction,
    setMessageModal,
    setEditExaminerNumberModalOpen,
    setPromptMainText,
    setPromptSubTexts,
    setIsConfirmationPromptVisible,
    setOpenDisableExaminerPrompt,
    setOpenEnableExaminerPrompt,
  });

  const getExaminingTeamMemberRow = useCallback(
    (examiningTeamMemberRowData: {
      assessmentId: string;
      examinerNumber: string;
      examCommittee: string;
      firstName: string;
      id: string;
      isBlocked: BlockadeTypeId | undefined;
      lastName: string;
      rowBackground: string;
      rowIndex: number;
      taskGroup: ITaskGroup;
      teamMember: ExaminingTeamMember;
    }) => {
      const {
        assessmentId,
        examinerNumber,
        examCommittee,
        firstName,
        id,
        isBlocked,
        lastName,
        rowBackground,
        rowIndex,
        taskGroup,
        teamMember,
      } = examiningTeamMemberRowData;

      return {
        id,
        function: (
          <div
            style={{
              transform: 'translateX(40px)',
            }}
          >
            {teamMember.examiner.examinerPermission[0]?.isVerifier ? 'Egzaminator-Weryfikator' : 'Egzaminator'}
          </div>
        ),
        firstName,
        lastName,
        examinerNumber: (
          <div className={classes.columnWithLockIcon}>
            {examinerNumber}
            {isBlocked === BlockadeTypeId.Manual && (
              <TooltipOne title="Zablokowany ręcznie" placement="top">
                <ManualLockedPersonIcon
                  className={classes.lockIcon}
                  style={{
                    color: fillRed,
                  }}
                />
              </TooltipOne>
            )}
            {isBlocked === BlockadeTypeId.Automatic && (
              <div className={classes.autolockIconDiv}>
                <TooltipOne title="Zablokowany automatycznie" placement="top">
                  <img
                    src={AutoLockedPersonIcon}
                    className={classes.autolockIcon}
                    alt="ikona blokady automatycznej"
                    style={{
                      color: fillRed,
                    }}
                  />
                </TooltipOne>
              </div>
            )}
          </div>
        ),
        examCommittee,
        actions: (
          <>
            {hasPermissionToManageActions && (
              <div
                onClick={() => {
                  handleClickMenu(
                    teamMember.examiner.examinerId,
                    taskGroup ?? undefined,
                    teamMember.examiner,
                    undefined,
                    undefined,
                    teamMember,
                    teamMember,
                    assessmentId,
                  );
                }}
              >
                {teamMember.examiner.examinerPermission[0]?.isVerifier
                  ? getVerifierActionsMenu(teamMember.examiner, assessmentId, rowIndex)
                  : getExaminingTeamMemberActionsMenu(teamMember.examiner, assessmentId, rowIndex)}
              </div>
            )}
          </>
        ),
        rowBackground,
      };
    },
    [
      classes.autolockIcon,
      classes.autolockIconDiv,
      classes.columnWithLockIcon,
      classes.lockIcon,
      getExaminingTeamMemberActionsMenu,
      getVerifierActionsMenu,
      handleClickMenu,
      hasPermissionToManageActions,
    ],
  );

  const updateRows = useCallback(
    (limit: number) => {
      const newData: ExaminerTreemapTableItem[] = [];
      let rowIndex = -1;

      const isUnderLimit = () => {
        return rowIndex < limit;
      };

      props.data.forEach((item: ITaskGroup) => {
        const assessmentId = item.assessmentSession?.assessmentId ?? '';
        rowIndex++;
        const taskGroupId = item.taskGroupId;
        const isTaskGroupIdExpanded = isExpanded(taskGroupId, expandedItems);
        const taskGroupRowData = {
          id: item.taskGroupId,
          isExpanded: isTaskGroupIdExpanded,
          item,
          tooltipTitle: isTaskGroupIdExpanded ? 'Zwiń grupę zadań' : 'Rozwiń grupę zadań',
          assessmentId,
          rowIndex,
        };

        if (isUnderLimit()) {
          const taskGroupRow = getNewRow(taskGroupId, taskGroupRowData, getTaskGroupRow);
          newData.push(taskGroupRow);
        }

        if (isTaskGroupIdExpanded) {
          if (item.sampleAssessmentGroup) {
            rowIndex++;

            const sampleAssessmentGroupRowId = item.sampleAssessmentGroup.sampleAssessmentsGroupId;
            const isSampleAssessmentGroupRowExpanded = isExpanded(sampleAssessmentGroupRowId, expandedItems);
            const sampleAssessmentGroupRowData = {
              id: sampleAssessmentGroupRowId,
              tooltipTitle: isSampleAssessmentGroupRowExpanded ? 'Zwiń funkcję' : 'Rozwiń funkcję',
              isExpanded: isSampleAssessmentGroupRowExpanded,
              taskGroup: item,
              assessmentId,
              rowIndex,
              itemIsSelected: sampleAssessmentGroupRowId === selectedItemId.current,
            };
            if (isUnderLimit()) {
              const sampleAssessmentGroupRow = getNewRow(
                sampleAssessmentGroupRowId,
                sampleAssessmentGroupRowData,
                getSampleAssessmentGroupRow,
              );
              newData.push(sampleAssessmentGroupRow);
            }

            if (isExpanded(item.sampleAssessmentGroup.sampleAssessmentsGroupId, expandedItems)) {
              rowIndex++;

              const { sampleAssessmentChairman } = item.sampleAssessmentGroup;
              const sampleAssessmentChairmanRowId = sampleAssessmentChairman.sampleAssessmentChairmanId;
              const rowBackground = determineRowBackgroundColor(
                sampleAssessmentChairman.sampleAssessmentChairmanId,
                sampleAssessmentChairman.examiner?.shouldBeHighlighted,
                assessmentId,
              );

              const sampleAssessmentChairmanRowData = {
                id: sampleAssessmentChairmanRowId,
                firstName: sampleAssessmentChairman?.examiner ? sampleAssessmentChairman.examiner.firstName : '-',
                lastName: sampleAssessmentChairman?.examiner ? sampleAssessmentChairman.examiner.lastName : '-',
                examinerNumber: getExaminerNumberFromPermission(sampleAssessmentChairman.examiner, assessmentId),
                examCommittee: sampleAssessmentChairman?.examiner
                  ? getCommitteeLabel(sampleAssessmentChairman.examiner?.examinerPermission[0]?.examCommitteeId)
                  : '-',
                sampleAssessmentGroup: item.sampleAssessmentGroup,
                assessmentId,
                taskGroup: item,
                rowIndex,
                rowBackground,
              };
              if (isUnderLimit()) {
                const sampleAssessmentChairmanRow = getNewRow(
                  sampleAssessmentChairmanRowId,
                  sampleAssessmentChairmanRowData,
                  getSampleAssessmentChairmanRow,
                );
                newData.push(sampleAssessmentChairmanRow);
              }

              if (item.sampleAssessmentGroup.sampleAssessmentMembers) {
                item.sampleAssessmentGroup.sampleAssessmentMembers.forEach((member) => {
                  rowIndex++;

                  const sampleAssessmentMemberId = member.sampleAssessmentMemberId;
                  const rowBackground = determineRowBackgroundColor(
                    member?.examiner.examinerId,
                    member?.examiner?.shouldBeHighlighted,
                    assessmentId,
                  );

                  const sampleAssessmentMemberRowData = {
                    assessmentId,
                    examinerNumber: getExaminerNumberFromPermission(member.examiner, assessmentId),
                    examCommittee: member?.examiner?.examinerPermission[0]?.examCommitteeId,
                    id: sampleAssessmentMemberId,
                    member,
                    rowBackground,
                    rowIndex,
                    taskGroup: item,
                  };

                  if (isUnderLimit()) {
                    const sampleAssessmentMemberRow = getNewRow(
                      sampleAssessmentMemberId,
                      sampleAssessmentMemberRowData,
                      getSampleAssessmentMemberRow,
                    );
                    newData.push(sampleAssessmentMemberRow);
                  }
                });
              }
            }
          }

          item.examTaskGroupCoordinators.forEach((examTaskGroupCoordinator) => {
            if (!examTaskGroupCoordinator.hasToBeHidden) {
              rowIndex++;

              const examTaskGroupCoordinatorId = examTaskGroupCoordinator.taskGroupCoordinatorId;

              const isExamTaskGroupCoordinatorExpanded = isExpanded(examTaskGroupCoordinatorId, expandedItems);
              const rowBackground = determineRowBackgroundColor(
                examTaskGroupCoordinator.taskGroupCoordinatorId,
                examTaskGroupCoordinator.examiner?.shouldBeHighlighted,
                assessmentId,
              );
              const examTaskGroupCoordinatorRowData = {
                assessmentId,
                examinerNumber: getExaminerNumberFromPermission(examTaskGroupCoordinator.examiner, assessmentId),
                examCommittee: examTaskGroupCoordinator?.examiner
                  ? getCommitteeLabel(examTaskGroupCoordinator.examiner?.examinerPermission[0]?.examCommitteeId)
                  : '-',
                examTaskGroupCoordinator,
                firstName: examTaskGroupCoordinator.examiner ? examTaskGroupCoordinator.examiner.firstName : '-',
                id: examTaskGroupCoordinatorId,
                isExpanded: isExamTaskGroupCoordinatorExpanded,
                lastName: examTaskGroupCoordinator.examiner ? examTaskGroupCoordinator.examiner.lastName : '-',
                rowBackground,
                rowIndex,
                taskGroup: item,
                tooltipTitle: isExamTaskGroupCoordinatorExpanded ? 'Zwiń funkcję' : 'Rozwiń funkcję',
              };
              if (isUnderLimit()) {
                const examTaskGroupCoordinatorRow = getNewRow(
                  examTaskGroupCoordinatorId,
                  examTaskGroupCoordinatorRowData,
                  getExamTaskGroupCoordinatorRow,
                );
                newData.push(examTaskGroupCoordinatorRow);
              }
            }

            if (
              isExpanded(examTaskGroupCoordinator.taskGroupCoordinatorId, expandedItems) ||
              (examTaskGroupCoordinator.hasToBeHidden && isExpanded(item.taskGroupId, expandedItems))
            ) {
              examTaskGroupCoordinator.examiningTeamChairmen.forEach((examiningTeamChairmen) => {
                rowIndex++;

                const examiningTeamChairmenid = examiningTeamChairmen.teamChairmanId;
                const isExaminingTeamChairmenExpanded = isExpanded(examiningTeamChairmen.teamChairmanId, expandedItems);
                const rowBackground = determineRowBackgroundColor(
                  examiningTeamChairmen.teamChairmanId,
                  examiningTeamChairmen.examiner?.shouldBeHighlighted,
                  assessmentId,
                );
                const examiningTeamChairmenRowData = {
                  assessmentId,
                  examinerNumber: getExaminerNumberFromPermission(examiningTeamChairmen.examiner, assessmentId),
                  examCommittee: examiningTeamChairmen?.examiner
                    ? getCommitteeLabel(examiningTeamChairmen.examiner?.examinerPermission[0]?.examCommitteeId)
                    : '-',
                  examiningTeamChairmen,
                  firstName: examiningTeamChairmen.examiner ? examiningTeamChairmen.examiner.firstName : '-',
                  id: examiningTeamChairmenid,
                  isBlocked: examiningTeamChairmen.examiner?.examinerPermission[0]?.isBlocked,
                  isRowExpanded: isExaminingTeamChairmenExpanded,
                  lastName: examiningTeamChairmen.examiner ? examiningTeamChairmen.examiner.lastName : '-',
                  rowBackground,
                  rowIndex,
                  taskGroup: item,
                  tooltipTitle: isExaminingTeamChairmenExpanded ? 'Zwiń funkcję' : 'Rozwiń funkcję',
                };
                if (isUnderLimit()) {
                  const examiningTeamChairmenRow = getNewRow(
                    examiningTeamChairmenid,
                    examiningTeamChairmenRowData,
                    getExaminingTeamChairmenRow,
                  );
                  newData.push(examiningTeamChairmenRow);
                }

                if (isExpanded(examiningTeamChairmen.teamChairmanId, expandedItems)) {
                  const members = examiningTeamChairmen.examiningTeam?.examiningTeamMembers ?? [];
                  members.forEach((teamMember) => {
                    rowIndex++;
                    const examiningTeamMemberId = `${examiningTeamChairmen.teamChairmanId}-${teamMember.examiner.examinerId}`;
                    const rowBackground = determineRowBackgroundColor(
                      teamMember.examiner.examinerId,
                      teamMember.examiner?.shouldBeHighlighted,
                      assessmentId,
                    );
                    const examiningTeamMemberRowData = {
                      assessmentId,
                      examinerNumber: getExaminerNumberFromPermission(teamMember.examiner, assessmentId),
                      examCommittee: getCommitteeLabel(teamMember.examiner?.examinerPermission[0]?.examCommitteeId),
                      firstName: teamMember.examiner.firstName,
                      id: examiningTeamMemberId,
                      isBlocked: teamMember.examiner?.examinerPermission[0].isBlocked,
                      lastName: teamMember.examiner.lastName,
                      rowBackground,
                      rowIndex,
                      taskGroup: item,
                      teamMember,
                    };
                    if (isUnderLimit()) {
                      const examiningTeamMemberRow = getNewRow(
                        examiningTeamMemberId,
                        examiningTeamMemberRowData,
                        getExaminingTeamMemberRow,
                      );

                      newData.push(examiningTeamMemberRow);
                    }
                  });
                }
              });
            }
          });
        }
      });

      allTreemapItemsCount.current = rowIndex + 1;
      if (renderStructureItemsCount > allTreemapItemsCount.current) {
        setRenderStructureItemsCount(Math.max(allTreemapItemsCount.current, treemapItemsPagination));
      }

      setTreemapData(newData);
    },
    [
      props.data,
      renderStructureItemsCount,
      expandedItems,
      getTaskGroupRow,
      getSampleAssessmentGroupRow,
      determineRowBackgroundColor,
      getSampleAssessmentChairmanRow,
      getSampleAssessmentMemberRow,
      getExamTaskGroupCoordinatorRow,
      getExaminingTeamChairmenRow,
      getExaminingTeamMemberRow,
    ],
  );

  useEffect(() => {
    updateRows(renderStructureItemsCount);
  }, [props.data, expandedItems, hasPermissionForGroupTaskActions, updateRows, renderStructureItemsCount]);

  return {
    allTreemapItemsCount,
    treemapData,
    renderStructureItemsCount,
    setRenderStructureItemsCount,
    isAddSampleAssessmentGroupConfirmationVisible,
    setIsAddSampleAssessmentGroupConfirmationVisible,
    openAddKGZPrompt,
    setOpenKGZPrompt,
    action,
    selectedSampleAsssessmentGroup,
    removedExaminatorFromZeByTeamMemberIdRef,
  };
};
